import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import type { PaperProps } from '@mui/material/Paper';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

export type AutocompleteMonoSelectProps<T> = {
  ariaLabel: string;
  disabled?: boolean;
  loading?: boolean;
  options?: T[];
  getOptionLabel: (option: T) => string;
  onSelect?: (value: T) => void;
  placeholder?: string;
  footer?: React.ReactNode;
  maxHeight?: string | number;
};

export function AutocompleteMonoSelect<T>({
  ariaLabel,
  disabled = false,
  loading = false,
  options = [],
  getOptionLabel,
  onSelect,
  placeholder,
  footer,
  maxHeight,
}: AutocompleteMonoSelectProps<T>) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const PaperComponent = useMemo(
    () =>
      footer
        ? ({ children, ...paperProps }: PaperProps) => (
            <Paper {...paperProps}>
              {children}
              {footer}
            </Paper>
          )
        : undefined,
    [footer]
  );

  const defaultListboxSx = {
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.action.disabledBackground} transparent`,
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.action.disabledBackground,
      borderRadius: '2px',
    },
    '.MuiAutocomplete-option': { backgroundColor: 'transparent' },
    '.MuiAutocomplete-option[data-focus="true"], .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: 'transparent',
    },
    '.MuiAutocomplete-option:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '.MuiAutocomplete-option:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '-2px',
      backgroundColor: 'transparent',
    },
  };

  return (
    <Autocomplete
      disablePortal
      size="small"
      options={options}
      loading={loading}
      loadingText={t('loading', 'Loading…')}
      noOptionsText={t('no_options', 'No options')}
      getOptionLabel={getOptionLabel}
      disabled={disabled}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        setInputValue(reason === 'reset' ? '' : value);
      }}
      onChange={(_, value) => {
        if (value) {
          onSelect?.(value);
        }
      }}
      PaperComponent={PaperComponent}
      ListboxProps={{
        style: maxHeight !== undefined ? { maxHeight } : undefined,
        sx: defaultListboxSx,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            'aria-label': ariaLabel,
            'aria-busy': loading,
            'data-dd-action-name': ariaLabel,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress aria-hidden color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
